<template>
  <v-dialog
    v-model="dialog"
    fullscreen
    hide-overlay
    transition="dialog-bottom-transition"
    content-class="engraving-generator-dialog-fullscreen"
  >
    <!-- pass through scoped slots -->
    <template v-for="(_, scopedSlotName) in $scopedSlots" v-slot:[scopedSlotName]="slotData">
      <slot :name="scopedSlotName" v-bind="slotData" />
    </template>

    <!-- pass through normal slots -->
    <template v-for="(_, slotName) in $slots" v-slot:[slotName]>
      <slot :name="slotName" />
    </template>

    <v-card class="engraving-generator-card" dark>
      <template v-for="(_, scopedSlotName) in $scopedSlots" v-slot:[scopedSlotName]="slotData">
        <slot :name="scopedSlotName" v-bind="slotData" />
      </template>
      <template v-for="(_, slotName) in $slots" v-slot:[slotName]>
        <slot :name="slotName" />
      </template>

      <div class="stepper-row-wrapper">
        <div class="engraving-generator-instructions-wrap">
          <v-btn
            icon
            text
            class="engraving-generator-instructions-btn"
            title="Instructions"
            aria-label="Instructions"
            @click="instructionsModalOpen = true"
          >
            <v-icon>mdi-information-outline</v-icon>
          </v-btn>
        </div>
        <StepperHeader
        :current-step="currentStep"
        :total-steps="totalSteps"
        :max-step-reached="maxStepReached"
        :is-next-disabled="isNextDisabled"
        :steps="steps"
        :has-edits-generated="hasEditsGenerated"
        @prev="goPrev"
        @next="goNext"
        @update:currentStep="currentStep = $event"
      />
        <div class="engraving-generator-cancel-wrap">
          <v-btn
            icon
            text
            class="engraving-generator-cancel"
            title="Cancel"
            @click="dialog = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </div>

      <div class="stepper-body">
        <v-row v-show="currentStep === 1" class="main-content ma-0" no-gutters>
          <v-col cols="12" md="3" class="pa-3">
            <OrderDetailsCard :order="order" />
          </v-col>
          <v-col cols="12" md="3" class="pa-3">
            <TypeSelectionCard
              :selected-tab.sync="selectedTab"
              :print-type.sync="printType"
              :prompt.sync="prompt"
              :model.sync="model"
              :tabs="tabs"
              :print-types="printTypes"
              :models="models"
            />
          </v-col>
          <v-col cols="12" md="6" class="pa-3">
            <div class="gallery-container">
              <ImageGallery
                class="step1-main-gallery"
                v-model="selectedIds"
                :items="step1GalleryItems"
                :allow-multiple="false"
                upload-button-align="right"
                @view="viewItem"
                @uploaded="uploadedFromGallery = $event"
                @nano-banana-deleted="onNanoBananaImageDeleted"
              >
                <template #upload-row-actions>
                  <div class="engraving-action-btns d-flex align-center flex-wrap">
                    <v-badge
                      :content="backsideEngravingLineItemsCount"
                      :value="hasBacksideEngravingLineItems"
                      color="primary"
                      overlap
                      bordered
                    >
                      <v-btn
                        small
                        outlined
                        class="gallery-upload-btn"
                        :disabled="!hasBacksideEngravingLineItems"
                        @click="backsideModalOpen = true"
                      >
                        <v-icon left small>mdi-text-box-outline</v-icon>
                        Backside Engraving
                      </v-btn>
                    </v-badge>
                    <v-badge
                      :content="otherSvgLineItemsCount"
                      :value="hasOtherSvgLineItems"
                      color="primary"
                      overlap
                      bordered
                    >
                      <v-btn
                        small
                        outlined
                        class="gallery-upload-btn"
                        :disabled="!hasOtherSvgLineItems"
                        @click="otherSvgsModalOpen = true"
                      >
                        <v-icon left small>mdi-vector-square</v-icon>
                        Other SVGs
                      </v-btn>
                    </v-badge>
                  </div>
                </template>
              </ImageGallery>
              <div v-if="backsideEngravingItems.length" class="step1-backside-section">
                <h4 class="step1-backside-heading">Backside engravings</h4>
                <ImageGallery
                  :items="backsideEngravingItems"
                  :selectable="false"
                  :show-upload-button="false"
                  image-key="imageUrl"
                  class="step1-backside-gallery"
                  @view="viewItem"
                  @nano-banana-deleted="onNanoBananaImageDeleted"
                />
              </div>
            </div>
          </v-col>
        </v-row>

        <Step2Content
          v-show="currentStep === 2"
          :loading="loading"
          :print-type-label="printTypeLabel"
          :print-type.sync="printType"
          :print-types="printTypes"
          :model.sync="model"
          :models="models"
          :selected-tab.sync="selectedTab"
          :prompt.sync="prompt"
          :predefined-prompt-text="predefinedPromptText"
          :edits="edits"
          :input-gallery-items="step1GalleryItems"
          :selected-ids.sync="selectedIds"
          :selected-items="selectedItems"
          :response-time="responseTime"
          :selected-edit-id.sync="selectedEditId"
          :edits-gallery-items="editsGalleryItems"
          :previously-generated-edits-items="previouslyGeneratedEditsItems"
          @generate="extractPrint"
          @view="viewItem"
          @nano-banana-deleted="onNanoBananaImageDeleted"
        />

        <Step3Content
          v-show="currentStep === 3"
          :order-number="order.orderNumber || ''"
          :mask-num-parts="maskNumParts"
          :mask-direction="maskDirection"
          :mask-shape="maskShape"
          :mask-spacing="maskSpacing"
          :mask-output-size="maskOutputSize"
          :mask-gallery-value="maskGalleryValue"
          :all-edits-for-mask-items="step3GalleryItems"
          :backside-engraving-items="backsideEngravingItems"
          :show-backside-button="hasBacksideEngravingLineItems"
          :backside-engraving-count="backsideEngravingLineItemsCount"
          :show-other-svgs-button="hasOtherSvgLineItems"
          :other-svgs-count="otherSvgLineItemsCount"
          :mask-images-ready="maskImagesReady"
          :mask-download-ready.sync="maskDownloadReady"
          :mask-image1-url="maskImage1Url"
          :mask-image2-url="maskImage2Url"
          :mask-image3-url="maskImage3Url"
          @open-mask-options="maskOptionsModalOpen = true"
          @open-backside="backsideModalOpen = true"
          @open-other-svgs="otherSvgsModalOpen = true"
          @mask-gallery-input="onMaskGalleryInput"
          @mask-uploaded="maskUploadedFromGallery = $event"
          @nano-banana-deleted="onNanoBananaImageDeleted"
        />
      </div>

      <MaskOptionsModal
        v-model="maskOptionsModalOpen"
        :num-parts="maskNumParts"
        :direction="maskDirection"
        :shape="maskShape"
        :spacing="maskSpacing"
        :output-size="maskOutputSize"
        @update:numParts="maskNumParts = $event"
        @update:direction="maskDirection = $event"
        @update:shape="maskShape = $event"
        @update:spacing="maskSpacing = $event"
        @update:outputSize="maskOutputSize = $event"
      />

      <InstructionsModal v-model="instructionsModalOpen" />

      <BacksideEngravingModal
        v-model="backsideModalOpen"
        :order="order"
        @generated="onBacksideGenerated"
      />

      <OtherSvgsModal v-model="otherSvgsModalOpen" :order="order" />
    </v-card>
  </v-dialog>
</template>

<script>
import { fetchFilteredImages } from '../../services/pdfGenerationService'
import { ImageGallery } from '@/components/ImageGallery'
import prompts from '@/assets/mizodia/custom-print-prompts.json'
import { STEPS, DEFAULT_OUTPUT_SIZE, TABS, PRINT_TYPES, MODELS } from './constants'
import { getUserUploadImages } from './orderHelpers'
import {
  BacksideEngravingModal,
  getBacksideEngravingLineItems,
  BACKSIDE_ENGRAVING_LINE_ITEM_IDS,
} from './BacksideEngraving'
import { OtherSvgsModal, getOtherSvgLineEntries } from './OtherSvgs'
import {
  buildStep1GalleryItems,
  buildEditsGalleryItems,
  buildPreviouslyGeneratedEditsItems,
  buildAllEditsItems,
  buildAllEditsForMask,
  buildStep3GalleryItems,
  dedupeByImageUrl,
  nanoBananaFilenameFromImageUrl,
} from './editsGalleryHelpers'
import { runExtractPrint } from './extractPrintRunner'
import OrderDetailsCard from './OrderDetailsCard.vue'
import StepperHeader from './StepperHeader.vue'
import TypeSelectionCard from './TypeSelectionCard.vue'
import Step2Content from './Step2Content.vue'
import Step3Content from './Step3Content.vue'
import MaskOptionsModal from './MaskOptionsModal.vue'
import InstructionsModal from './InstructionsModal.vue'

export default {
  name: 'EngravingGenerator',
  components: {
    ImageGallery,
    OrderDetailsCard,
    StepperHeader,
    TypeSelectionCard,
    Step2Content,
    Step3Content,
    MaskOptionsModal,
    InstructionsModal,
    BacksideEngravingModal,
    OtherSvgsModal,
  },
  props: {
    order: { type: Object, required: true },
  },
  data() {
    return {
      dialog: false,
      currentStep: 1,
      totalSteps: 3,
      loading: false,
      selectedIds: [],
      galleryItems: [],
      uploadedFromGallery: [],
      orderInputItems: [],
      backendGalleryItems: [],
      backendFilteredItems: [],
      sessionEdits: [],
      maxStepReached: 1,

      tabs: TABS,
      selectedTab: 0,
      printType: 'fingerprint',
      prompt: prompts['fingerprint'] || '',
      printTypes: PRINT_TYPES,
      model: 'gemini-2.5-flash-image',
      models: MODELS,
      responseTime: null,
      previewSrc: null,
      edits: [],
      originalGeminiEdits: [],
      selectedEditId: null,
      lastGeneratedImageUrl: null,

      maskNumParts: 1,
      maskShape: 'circle',
      maskDirection: 'horizontal',
      maskSpacing: 0,
      maskOutputSize: DEFAULT_OUTPUT_SIZE,
      maskImage1Url: null,
      maskImage2Url: null,
      maskImage3Url: null,
      maskDownloadReady: false,
      maskSelectedIds: [],
      maskOptionsModalOpen: false,
      instructionsModalOpen: false,
      maskUploadedFromGallery: [],
      backsideModalOpen: false,
      otherSvgsModalOpen: false,
      backsideEngravingItems: [],
      /** url -> server filename for nanoBanana delete (only generate + images/filter). */
      nanoBananaFileByUrl: {},
    }
  },
  computed: {
    steps: () => STEPS,
    /** Resolved type key (string) for prompts: list may emit index or value. */
    effectivePrintType() {
      return typeof this.printType === 'number'
        ? (this.printTypes[this.printType]?.value || 'fingerprint')
        : this.printType
    },
    /** Human-readable label for the selected print type (for Step 2 badge). */
    printTypeLabel() {
      const type = this.effectivePrintType
      const found = (this.printTypes || []).find((t) => t.value === type)
      return found ? found.label : type || '—'
    },
    /** Built-in prompt for current type (shown when mode is Default). */
    predefinedPromptText() {
      return prompts[this.effectivePrintType] || ''
    },
    step1GalleryItems() {
      const base = buildStep1GalleryItems(this.uploadedFromGallery, this.orderInputItems)
      return base.map((item) => ({
        ...item,
        nanoBananaFilename: this.nanoBananaFileByUrl[item.imageUrl] || null,
      }))
    },
    editsGalleryItems() {
      return buildEditsGalleryItems(this.edits).map((item) => ({
        ...item,
        nanoBananaFilename: this.nanoBananaFileByUrl[item.imageUrl] || null,
      }))
    },
    allEditsForMask() {
      return buildAllEditsForMask(this.allEditsItems, this.maskUploadedFromGallery)
    },
    allEditsForMaskWithoutUploads() {
      return this.allEditsItems || []
    },
    /** Step 3 left gallery: AI edits + step-3 uploads + step 1 images + backside engravings (for mask selection). */
    step3GalleryItems() {
      const step1 = buildStep1GalleryItems(this.uploadedFromGallery, this.orderInputItems).map(
        (item) => ({
          ...item,
          nanoBananaFilename: this.nanoBananaFileByUrl[item.imageUrl] || null,
        })
      )
      const main = buildStep3GalleryItems(this.allEditsForMask, step1)
      const withMeta = main.map((item) => ({
        ...item,
        nanoBananaFilename: this.nanoBananaFileByUrl[item.imageUrl] || null,
      }))
      return dedupeByImageUrl([...withMeta, ...(this.backsideEngravingItems || [])])
    },
    maskImagesReady() {
      if (this.maskNumParts === 1) return !!this.maskImage1Url
      if (this.maskNumParts === 2) return !!this.maskImage1Url && !!this.maskImage2Url
      return !!this.maskImage1Url && !!this.maskImage2Url && !!this.maskImage3Url
    },
    maskImage1SelectedId() {
      const item = (this.step3GalleryItems || []).find((i) => i.imageUrl === this.maskImage1Url)
      return item ? item.id : null
    },
    maskImage2SelectedId() {
      const item = (this.step3GalleryItems || []).find((i) => i.imageUrl === this.maskImage2Url)
      return item ? item.id : null
    },
    maskImage3SelectedId() {
      const item = (this.step3GalleryItems || []).find((i) => i.imageUrl === this.maskImage3Url)
      return item ? item.id : null
    },
    /** Value for the single gallery: single id when 1 part, array when 2 or 3. */
    maskGalleryValue() {
      if (this.maskNumParts === 1) return this.maskImage1SelectedId
      const ids = this.maskSelectedIds || []
      return ids.slice(0, this.maskNumParts)
    },
    /** Previous runs (session minus current) on top, then backend edits. So last request’s edits show on top of “Previously generated”. */
    previouslyGeneratedEditsItems() {
      const base = buildPreviouslyGeneratedEditsItems({
        sessionEdits: this.sessionEdits,
        edits: this.edits,
        backendFilteredItems: this.backendFilteredItems,
      })
      return base.map((item) => ({
        ...item,
        nanoBananaFilename: this.nanoBananaFileByUrl[item.imageUrl] || null,
      }))
    },
    allEditsItems() {
      const prevBase = buildPreviouslyGeneratedEditsItems({
        sessionEdits: this.sessionEdits,
        edits: this.edits,
        backendFilteredItems: this.backendFilteredItems,
      })
      const base = buildAllEditsItems(this.sessionEdits, prevBase)
      return base.map((item) => ({
        ...item,
        nanoBananaFilename: this.nanoBananaFileByUrl[item.imageUrl] || null,
      }))
    },
    /** Selected input image(s) from step 1 / step 2 left gallery (input images only). */
    selectedItems() {
      const ids = Array.isArray(this.selectedIds)
        ? this.selectedIds
        : this.selectedIds != null && this.selectedIds !== ''
          ? [this.selectedIds]
          : []
      if (!ids.length) return []
      return this.step1GalleryItems.filter((item) => ids.includes(item.id))
    },
    isNextDisabled() {
      if (this.loading) return true
      if (this.currentStep === 1) return !this.selectedItems.length
      return false
    },
    /** True when at least one edit exists (this session or previously from backend); unlocks steps 2 and 3. */
    hasEditsGenerated() {
      const session = (this.sessionEdits || []).length > 0
      const backend = (this.backendFilteredItems || []).length > 0
      return session || backend
    },
    /** True when the order has at least one line item allowed for backside engraving (by id). */
    hasBacksideEngravingLineItems() {
      return getBacksideEngravingLineItems(this.order, BACKSIDE_ENGRAVING_LINE_ITEM_IDS).length > 0
    },
    /** Number of line items allowed for backside engraving (for badge). */
    backsideEngravingLineItemsCount() {
      return getBacksideEngravingLineItems(this.order, BACKSIDE_ENGRAVING_LINE_ITEM_IDS).length
    },
    otherSvgLineEntries() {
      return getOtherSvgLineEntries(this.order)
    },
    hasOtherSvgLineItems() {
      return this.otherSvgLineEntries.length > 0
    },
    otherSvgLineItemsCount() {
      return this.otherSvgLineEntries.length
    },
  },
  watch: {
    dialog: {
      handler: async function (v) {
        this.dialog = !!v
        if (!v) return

        this.maxStepReached = 1
        this.selectedIds = []
        this.backsideEngravingItems = []

        const userUploads = getUserUploadImages(this.$props.order)
        this.orderInputItems = userUploads || []
        this.sessionEdits = []
        this.uploadedFromGallery = []
        this.maskUploadedFromGallery = []
        this.nanoBananaFileByUrl = {}

        const response = await fetchFilteredImages(this.$props.order.orderNumber)
        const data = response?.data
        if (data?.success) {
          const files = data.data.files || []
          files.forEach((f) => {
            const u = f.url
            if (!u) return
            const fn =
              (f.filename && String(f.filename).trim()) ||
              (f.fileName && String(f.fileName).trim()) ||
              nanoBananaFilenameFromImageUrl(u)
            if (fn) this.$set(this.nanoBananaFileByUrl, u, fn)
          })
          const backendItems = files.map(({ url }) => ({
            id: url,
            imageUrl: url,
          }))
          this.backendFilteredItems = backendItems
          this.backendGalleryItems = (userUploads || []).concat(backendItems)
        } else {
          this.backendFilteredItems = []
          this.backendGalleryItems = userUploads || []
        }
        this.refreshGalleryItems()
      },
    },
    maskNumParts(val) {
      this.maskSelectedIds = (this.maskSelectedIds || []).slice(0, val)
      const items = this.step3GalleryItems || []
      this.maskImage1Url = this.maskSelectedIds[0] ? (items.find((i) => i.id === this.maskSelectedIds[0]) || {}).imageUrl : null
      this.maskImage2Url = val >= 2 && this.maskSelectedIds[1] ? (items.find((i) => i.id === this.maskSelectedIds[1]) || {}).imageUrl : null
      this.maskImage3Url = val >= 3 && this.maskSelectedIds[2] ? (items.find((i) => i.id === this.maskSelectedIds[2]) || {}).imageUrl : null
    },
    /** Sync maskSelectedIds from URL-based selection when ids are empty (e.g. initial load). */
    maskImage1SelectedId: 'syncMaskSelectedIdsFromUrls',
    maskImage2SelectedId: 'syncMaskSelectedIdsFromUrls',
    maskImage3SelectedId: 'syncMaskSelectedIdsFromUrls',
  },
  methods: {
    refreshGalleryItems() {
      const sessionItems = (this.sessionEdits || []).map((url, i) => ({
        id: `session-${i}`,
        imageUrl: url,
      }))
      const byUrl = new Map()
      ;[...(this.backendGalleryItems || []), ...sessionItems].forEach((item) => {
        const url = item.imageUrl
        if (!byUrl.has(url)) byUrl.set(url, { ...item, id: item.id || url })
      })
      this.galleryItems = Array.from(byUrl.values())
    },
    onDialogInput(open) {
      if (!open) this.$emit('input', false)
    },
    onBacksideGenerated({ url }) {
      if (!url) return
      const id = `backside-${Date.now()}-${this.backsideEngravingItems.length}`
      this.backsideEngravingItems = [...this.backsideEngravingItems, { id, imageUrl: url, label: 'Backside' }]
    },
    async goNext() {
      if (this.currentStep === 1) {
        this.currentStep = 2
        this.maxStepReached = Math.max(this.maxStepReached, 2)
        // Always generate when moving to step 2 with a selection (so back + Next resends with current params)
        if (this.selectedItems.length) {
          this.$nextTick(() => this.extractPrint())
        }
        return
      }
      if (this.currentStep < this.totalSteps) {
        this.currentStep += 1
        this.maxStepReached = Math.max(this.maxStepReached, this.currentStep)
      } else {
        this.dialog = false
      }
    },
    goPrev() {
      if (this.currentStep > 1) this.currentStep -= 1
    },
    async extractPrint() {
      const firstSelected = this.selectedItems[0]
      const imageUrl = firstSelected?.imageUrl
      if (!imageUrl || typeof imageUrl !== 'string') return

      // Push current newly generated into "previously generated" before loading so they stay visible
      this.sessionEdits = [...(this.edits || []), ...(this.sessionEdits || [])]
      this.edits = []
      this.selectedEditId = null

      this.loading = true
      try {
        const result = await runExtractPrint({
          imageUrl,
          selectedTab: this.selectedTab,
          printType: this.printType,
          printTypes: this.printTypes,
          prompt: this.prompt,
          model: this.model,
          models: this.models,
          orderNumber: this.$props.order?.orderNumber,
        })
        const { edits, responseTime, originalGeminiEdits, nanoBananaEditMeta } = result
        for (const row of nanoBananaEditMeta || []) {
          if (row.url && row.filename) this.$set(this.nanoBananaFileByUrl, row.url, row.filename)
        }
        this.previewSrc = edits?.[0]
        this.edits = edits
        this.lastGeneratedImageUrl = imageUrl
        this.sessionEdits = [...edits, ...this.sessionEdits]
        this.refreshGalleryItems()
        this.originalGeminiEdits = originalGeminiEdits || []
        this.selectedEditId = null
        this.responseTime = responseTime
      } catch (err) {
        console.error('extractPrint error:', err)
      } finally {
        this.loading = false
        const audio = new Audio(
          'https://actions.google.com/sounds/v1/cartoon/wood_plank_flicks.ogg'
        )
        audio.play()
      }
    },
    viewItem(item) {
      this.$emit('view', item)
    },
    onNanoBananaImageDeleted({ url }) {
      if (!url) return
      const idSet = new Set()
      ;(this.step3GalleryItems || []).forEach((i) => {
        if (i.imageUrl === url) idSet.add(i.id)
      })
      this.$delete(this.nanoBananaFileByUrl, url)
      this.sessionEdits = (this.sessionEdits || []).filter((u) => u !== url)
      this.edits = (this.edits || []).filter((u) => u !== url)
      this.backendFilteredItems = (this.backendFilteredItems || []).filter((i) => i.imageUrl !== url)
      this.backendGalleryItems = (this.backendGalleryItems || []).filter((i) => i.imageUrl !== url)
      this.originalGeminiEdits = (this.originalGeminiEdits || []).filter((u) => u !== url)
      this.maskUploadedFromGallery = (this.maskUploadedFromGallery || []).filter(
        (i) => i.imageUrl !== url
      )
      if (this.previewSrc === url) this.previewSrc = null
      this.maskSelectedIds = (this.maskSelectedIds || []).filter((id) => !idSet.has(id))
      if (this.selectedEditId != null && idSet.has(this.selectedEditId)) this.selectedEditId = null
      this.refreshGalleryItems()
      this.$nextTick(() => {
        const items = this.step3GalleryItems || []
        const pick = (idx) => {
          const sid = (this.maskSelectedIds || [])[idx]
          return sid ? items.find((i) => i.id === sid) : null
        }
        this.maskImage1Url = pick(0)?.imageUrl ?? null
        this.maskImage2Url = this.maskNumParts >= 2 ? pick(1)?.imageUrl ?? null : null
        this.maskImage3Url = this.maskNumParts >= 3 ? pick(2)?.imageUrl ?? null : null
      })
    },
    onMaskGalleryInput(val) {
      const items = this.step3GalleryItems || []
      if (this.maskNumParts === 1) {
        const id = val != null && val !== '' ? val : null
        this.maskSelectedIds = id ? [id] : []
        const item = id ? items.find((i) => i.id === id) : null
        this.maskImage1Url = item ? item.imageUrl : null
        this.maskImage2Url = null
        this.maskImage3Url = null
      } else {
        const arr = Array.isArray(val) ? val.slice(0, this.maskNumParts) : []
        this.maskSelectedIds = arr
        this.maskImage1Url = arr[0] ? (items.find((i) => i.id === arr[0]) || {}).imageUrl : null
        this.maskImage2Url = arr[1] ? (items.find((i) => i.id === arr[1]) || {}).imageUrl : null
        this.maskImage3Url = arr[2] ? (items.find((i) => i.id === arr[2]) || {}).imageUrl : null
      }
    },
    syncMaskSelectedIdsFromUrls() {
      if ((this.maskSelectedIds || []).length > 0) return
      const ids = [this.maskImage1SelectedId, this.maskImage2SelectedId, this.maskImage3SelectedId].filter(Boolean).slice(0, this.maskNumParts)
      if (ids.length > 0) this.maskSelectedIds = ids
    },
  },
}
</script>

<style scoped>
.engraving-generator-card {
  background: linear-gradient(180deg, #25262a 0%, #1c1d21 100%) !important;
  border-radius: 0;
  overflow: hidden;
  padding: 32px 28px 0 28px;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  height: 100%;
  box-shadow: none;
}

.engraving-generator-card .stepper-body {
  flex: 1;
  min-height: 280px;
}

.stepper-row-wrapper {
  display: flex;
  align-items: flex-start;
  gap: 0;
}

.stepper-row-wrapper .stepper-row {
  flex: 1;
  min-width: 0;
}

.engraving-generator-instructions-wrap {
  flex-shrink: 0;
  width: 56px;
  min-width: 56px;
  height: 44px;
  margin-top: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.engraving-generator-instructions-btn {
  color: rgba(255, 255, 255, 0.7) !important;
}

.engraving-generator-instructions-btn:hover {
  color: rgba(255, 255, 255, 0.95) !important;
}

.engraving-generator-cancel-wrap {
  flex-shrink: 0;
  width: 56px;
  min-width: 56px;
  height: 44px;
  margin-top: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.engraving-generator-cancel {
  color: rgba(255, 255, 255, 0.7) !important;
}

.engraving-generator-cancel:hover {
  color: #fff !important;
  background: #e81123 !important;
}

.gallery-container {
  padding-top: 0.5rem;
}

.engraving-action-btns {
  gap: 8px;
}

/* Main gallery grid: shrink to content so no empty space when few images */
.step1-main-gallery >>> .gallery-grid {
  height: auto;
  min-height: 0;
  max-height: 28rem;
}

.step1-backside-section {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.step1-backside-heading {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(255, 255, 255, 0.6);
  margin: 0 0 12px;
  padding: 0;
  text-align: left;
}

.step1-backside-gallery {
  max-height: 12rem;
  overflow-y: auto;
}

.step1-backside-gallery >>> .gallery-grid {
  height: auto;
  min-height: 0;
}

.stepper-body {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding-top: 8px;
}

.stepper-content {
  padding: 0 0 12px !important;
  min-height: 0;
}

/* Order details card – polished, soft UI */
.order-details-card {
  height: 100%;
  min-height: 280px;
  text-align: left;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.06) 0%, rgba(255, 255, 255, 0.02) 100%) !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  border-radius: 12px !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.05);
  overflow: hidden;
}

.order-details-card__title {
  font-size: 0.9375rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.12em !important;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.85) !important;
  padding: 20px 22px 16px !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.order-details-card__divider {
  display: none;
}

.order-details-card__body {
  padding: 18px 20px 22px !important;
}

/* Section boxes – soft panels with consistent styling */
.order-details__box {
  padding: 14px 16px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 12px;
  margin-bottom: 14px;
  transition: background 0.2s ease, border-color 0.2s ease;
}

.order-details__box:last-child {
  margin-bottom: 0;
}

.order-details__box:hover {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.09);
}

.order-details__box-label {
  display: block;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 10px;
  line-height: 1.2;
}

.order-details__box-num {
  display: inline-block;
  font-size: 1.375rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  color: #fff;
  line-height: 1.2;
  padding: 4px 0;
}

.order-details__box-value {
  font-size: 0.875rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.55;
  letter-spacing: 0.01em;
}

.order-details__box-value--prewrap {
  white-space: pre-wrap;
  word-break: break-word;
}

.order-details__box-primary {
  display: block;
  font-size: 0.9375rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  margin-bottom: 4px;
  letter-spacing: 0.01em;
}

.order-details__box-meta {
  display: block;
  font-size: 0.8125rem;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.55);
  line-height: 1.45;
  margin-top: 2px;
}

.order-details__box-meta:first-of-type {
  margin-top: 8px;
}

.order-details__box--list {
  padding-bottom: 10px;
}

.order-details__box--list:hover {
  background: rgba(255, 255, 255, 0.04);
}

.order-details__list {
  background: transparent !important;
  margin-top: 8px;
}

.order-details__list-item {
  min-height: 48px;
  padding: 10px 12px 10px 10px !important;
  margin: 0 -10px 0 -12px;
  border-radius: 8px;
  border-bottom: none;
  align-items: flex-start;
  transition: background 0.15s ease;
}

.order-details__list-item:hover {
  background: rgba(255, 255, 255, 0.06);
}

.order-details__list-item:last-child {
  border-bottom: none;
}

.order-details__list-content {
  padding: 0 !important;
}

.order-details__list-title {
  font-size: 0.875rem !important;
  font-weight: 600 !important;
  color: rgba(255, 255, 255, 0.92) !important;
  line-height: 1.4;
  letter-spacing: 0.01em;
}

.order-details__list-qty {
  font-size: 0.75rem !important;
  font-weight: 500 !important;
  color: rgba(255, 255, 255, 0.5) !important;
  margin-top: 4px;
}

.order-details__box--list .order-details__list {
  max-height: 180px;
  padding-right: 4px;
}

.order-details__box--list .order-details__list::-webkit-scrollbar {
  width: 6px;
}

.order-details__box--list .order-details__list::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.04);
  border-radius: 3px;
}

.order-details__box--list .order-details__list::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.15);
  border-radius: 3px;
}

.order-details__empty {
  font-size: 0.8125rem;
  color: rgba(255, 255, 255, 0.38);
  margin: 12px 0 0;
  font-style: italic;
  line-height: 1.5;
}

/* Section titles */
.section-title {
  font-size: 0.9375rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  text-align: left;
  padding: 18px 20px 14px !important;
  color: rgba(255, 255, 255, 0.9) !important;
}

.section-divider {
  margin: 0 20px !important;
  border-color: rgba(255, 255, 255, 0.06) !important;
}

.section-body {
  text-align: left;
  padding: 18px 20px 20px !important;
  padding-top: 16px !important;
}

/* Type selection – tabs and list */
.type-tabs {
  margin-bottom: 16px !important;
}

.type-tabs >>> .v-tabs-bar {
  background: transparent !important;
}

.type-tabs >>> .v-tab {
  text-transform: none !important;
  font-size: 0.8125rem !important;
  font-weight: 600 !important;
  letter-spacing: 0.02em;
}

.type-tabs >>> .v-tab--active {
  color: #64b5f6 !important;
}

.type-list {
  background: transparent !important;
  border: 1px solid rgba(255, 255, 255, 0.06) !important;
  border-radius: 10px;
  overflow: hidden;
}

.type-list .type-list-item {
  min-height: 44px;
  padding-left: 12px !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  transition: background 0.15s ease;
}

.type-list .type-list-item:last-child {
  border-bottom: none;
}

.type-list .type-list-item:hover {
  background: rgba(255, 255, 255, 0.04) !important;
}

.type-list .type-list-item.v-list-item--active {
  background: rgba(66, 165, 245, 0.15) !important;
  border-left: 3px solid #42a5f5;
  padding-left: 9px !important; /* 9px + 3px border = 12px so text doesn’t shift */
}

.type-list-item-title {
  font-size: 0.8125rem !important;
  font-weight: 500 !important;
  color: rgba(255, 255, 255, 0.88) !important;
}

</style>

<style lang="scss">
/* Unscoped: dialog is rendered in v-app so fullscreen must apply globally */
.engraving-generator-dialog-fullscreen.v-dialog {
  margin: 0 !important;
  max-width: 100% !important;
  width: 100% !important;
  height: 100% !important;
  min-height: 100vh !important;
  background: linear-gradient(180deg, #25262a 0%, #1c1d21 100%) !important;
}
</style>
